import {
	Wizard,
	WizardProps
} from '@cloudscape-design/components'

import { useOktaAuth } from '@okta/okta-react'
import { FC, useCallback, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { useNewPlatformFormStore } from '../../../stores/platforms/newPlatformFormStore'
import { useGenericTagsManagementFormStore } from '../../../stores/tags/genericTagsManagementFormStore'
import useCreateNewPlatform from '../../../hooks/mutate/useCreateNewPlatform'
import type { PlatformRequest } from '../../../types/platforms'
import { getTagsApprovalObject, TagsRequest } from '../../../types/tags'
import { v4 as uuidv4 } from 'uuid'
import useCreateNewTags from '../../../hooks/mutate/useCreateNewTags'
import GcpClusterDetails from '../../../components/gcpClusterOnboardingCreation/Steps/01-cluster-details'
import GcpClusterStrategy from '../../../components/gcpClusterOnboardingCreation/Steps/02-cluster-strategy'
import GcpResourceTags from '../../../components/gcpClusterOnboardingCreation/Steps/03-resource-tags'
import Review from '../../../components/gcpClusterOnboardingCreation/Steps/04-review'

const NewPlatformForm: FC = () => {
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const clusterCreationFormStore = useNewPlatformFormStore()
	const tagCreationFormStore = useGenericTagsManagementFormStore()
	const stepValidators = useRef<Record<number, () => Promise<boolean>>>({})

	const registerStepValidation = useCallback(
		(stepIndex: number, validate: () => Promise<boolean>) => {
			stepValidators.current[stepIndex] = validate
		},
		[]
	)

	const i18nStrings: WizardProps.I18nStrings = {
		stepNumberLabel: (stepNumber: number) => `Step ${stepNumber}`,
		collapsedStepsLabel: (stepNumber: number, stepsCount: number) =>
			`Step ${stepNumber} of ${stepsCount}`,
		skipToButtonLabel: (step: {title: string}, _stepNumber: number) =>
			`Skip to ${step.title}`,
		navigationAriaLabel: "Steps",
		cancelButton: "Cancel",
		previousButton: "Previous",
		nextButton: "Next",
		submitButton: "Submit",
		optional: "optional"
	}

	const steps: Array<WizardProps.Step> = [
		{
			title: 'Cluster Details',
			content: (
				<GcpClusterDetails
					registerValidation={(validate) => registerStepValidation(0, validate)}
				/>
			)
		},
		{
			title: 'Cluster Strategy',
			content: (
				<GcpClusterStrategy
					registerValidation={(validate) => registerStepValidation(1, validate)}
				/>
			)
		},
		{
			title: 'Resource Tags',
			content: (
				<GcpResourceTags
					registerValidation={(validate) => registerStepValidation(2, validate)}
				/>
			)
		},
		{
			title: 'Review',
			content: <Review />
		}
	]

	const handleWizardNavigate = async ({
		detail
	}: {
		detail: WizardProps.NavigateDetail
	}) => {
		const currentStep = clusterCreationFormStore.currentStep
		const isMovingForward = detail.requestedStepIndex > currentStep

		if (isMovingForward) {
			const validateCurrentStep =
				stepValidators.current[currentStep]

			if (validateCurrentStep && !(await validateCurrentStep())) return
		}

		clusterCreationFormStore.actions.setCurrentStep(detail.requestedStepIndex)
	}



	const navigate = useNavigate()
	const {
		formValues,
		actions: {clearFormValues }
	} = useNewPlatformFormStore()

	const {
		formValues: tagFormValues,
		actions: { clearFormValues: clearTagFormValues }
	} = useGenericTagsManagementFormStore()

	const { mutate: createNewPlatform, isLoading } = useCreateNewPlatform()
	const { mutate: createTags, isLoading: isCreateTagsLoading } = useCreateNewTags()
	const {
		setValue,
	} = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: formValues
	})

	const handleCancel = () => {
		clusterCreationFormStore.actions.clearFormValues()
		clusterCreationFormStore.actions.setCurrentStep(0)
		tagCreationFormStore.actions.clearFormValues()
		navigate(-1)
	}


	const handleSubmit = async () => {
		console.log(clusterCreationFormStore.formValues)
		const inheritedTagsId = typeof formValues.tagsID === 'string' ? formValues.tagsID : ''
		const isUsingInheritedTags = Boolean(formValues.inheritGcpTagsBool)

		if (isUsingInheritedTags && !inheritedTagsId) {
			console.error('GCP cluster onboarding requires inherited resource tags.')
			return
		}

		const fundingChoice = isUsingInheritedTags ? 'inherit' : 'own'
		const parentTagsID = String(formValues.parentTagsID || inheritedTagsId || '')
		const createPlatform = (tagsId: string) => {
			const platform: PlatformRequest = {
				requestorID: thisUserEmail,
				tagsID: tagsId,
				action: 'CREATE',
				details: {
					...formValues,
					tagsID: tagsId,
					fundingChoice,
					parentTagsID,
					inheritGcpTagsBool: isUsingInheritedTags
				},
				status: 'PENDING_APPROVAL',
				creationTime: new Date().toISOString()
			}

			createNewPlatform(platform, {
				onSuccess: () => {
					navigate('/cluster')
					clearFormValues()
					clearTagFormValues()
					window.location.reload()
				},
				onError: (e) => console.error(e)
			})
		}

		const newTagsId = uuidv4()
		const newTag: TagsRequest = {
			requestorID: thisUserEmail,
			tagsID: newTagsId,
			parentTagsID,
			approvalStatus: getTagsApprovalObject(),
			details: {
				...tagFormValues,
				fundingChoice,
				parentTagsID
			},
			action: 'CREATE',
			status: 'PENDING_APPROVAL'
		}

		createTags(newTag, {
			onSuccess: () => createPlatform(newTagsId),
			onError: (e) => console.error(e)
		})
	}
	useEffect(() => {
		setValue('region', 'Singapore (asia-southeast1)')
	}, [setValue])

	return (
		<Wizard
			i18nStrings={i18nStrings}
			steps={steps}
			activeStepIndex={clusterCreationFormStore.currentStep}
			onNavigate={handleWizardNavigate}
			onSubmit={handleSubmit}
			isLoadingNextStep={isLoading || isCreateTagsLoading}
			onCancel={handleCancel}
			/>
	)
}

export default NewPlatformForm
